import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, Search, X, Loader2, ArrowUpDown, ArrowUp, ArrowDown,
  Activity, Clock, Hash, Cpu, Flame, Timer, BarChart2,
  CheckCircle2, AlertCircle
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
} from 'recharts';
import baseapi from '../../../services/baseapi';
import { DBExportModal, type ColumnItem } from './DBExportModal';

interface UserQueryStatisticsViewProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
  dbType?:string;
}

export const UserQueryStatisticsView: React.FC<UserQueryStatisticsViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
  dbType
}) => {
  const [loading, setLoading] = useState(true);
  const [userQueryStats, setUserQueryStats] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [insights, setInsights] = useState<any[]>([]);
  const [chartMetric, setChartMetric] = useState<'time' | 'calls' | 'latency'>('time');
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'username' | 'calls' | 'avg_time' | 'total_time' | 'cpu_time' | 'share'>('total_time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const effectiveDbType = (dbType || '').toLowerCase();
  const isMariaDB = effectiveDbType.includes('mariadb') || (activeCluster || '').toLowerCase().includes('mariadb');

  // Dynamic Dark/Light Theme Detection via MutationObserver
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false
  );

  useEffect(() => {
    const checkDark = () => setIsDark(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const params: any = { cluster_id: activeCluster };
    if (clientId) params.client_id = clientId;

    try {
      const userRes = await baseapi.get('/db_monitor_dashboard/user_query_stats', { params });
      const raw = userRes.data;
      const uData = raw?.data || (Array.isArray(raw) ? raw : []);
      const sData = raw?.summary || null;
      const iData = raw?.insights || [];
      setUserQueryStats(Array.isArray(uData) ? uData : []);
      setSummary(sData);
      setInsights(Array.isArray(iData) ? iData : []);
    } catch (err) {
      console.warn('UserQueryStatisticsView fetch error:', err);
      setUserQueryStats([]);
      setSummary(null);
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const handleSort = (field: 'username' | 'calls' | 'avg_time' | 'total_time' | 'cpu_time' | 'share') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }: { field: 'username' | 'calls' | 'avg_time' | 'total_time' | 'cpu_time' | 'share' }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-500 inline-block ml-1 opacity-60" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-orange-400 inline-block ml-1" />
    ) : (
      <ArrowDown className="w-3 h-3 text-orange-400 inline-block ml-1" />
    );
  };

  const filteredAndSortedStats = useMemo(() => {
    const list = userQueryStats.filter((u) => {
      if (!userSearchQuery.trim()) return true;
      return u.username && u.username.toLowerCase().includes(userSearchQuery.toLowerCase().trim());
    });

    return list.sort((a, b) => {
      let aVal: any = 0;
      let bVal: any = 0;

      if (sortBy === 'username') {
        aVal = a.username || '';
        bVal = b.username || '';
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else if (sortBy === 'calls') {
        aVal = Number(a.calls || 0);
        bVal = Number(b.calls || 0);
      } else if (sortBy === 'avg_time') {
        const aCalls = Number(a.calls || 0);
        const aTotal = Number(a.total_time_ms || 0);
        aVal = aCalls > 0 ? aTotal / aCalls : 0;

        const bCalls = Number(b.calls || 0);
        const bTotal = Number(b.total_time_ms || 0);
        bVal = bCalls > 0 ? bTotal / bCalls : 0;
      } else if (sortBy === 'total_time') {
        aVal = Number(a.total_time_ms || 0);
        bVal = Number(b.total_time_ms || 0);
      } else if (sortBy === 'cpu_time') {
        aVal = Number(a.cpu_time_ms || 0);
        bVal = Number(b.cpu_time_ms || 0);
      } else if (sortBy === 'share') {
        aVal = Number(a.total_time_ms || 0);
        bVal = Number(b.total_time_ms || 0);
      }

      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [userQueryStats, userSearchQuery, sortBy, sortOrder]);

  const exportColumns = useMemo<ColumnItem[]>(() => [
    { key: 'username', label: 'User' },
    { key: 'calls', label: isMariaDB ? 'Statements' : 'Queries' },
    { key: 'avg_time_ms', label: 'Avg Time (ms)' },
    { key: 'total_time_ms', label: 'Total Time (ms)' },
    { key: 'cpu_time_ms', label: 'CPU Time (ms)' },
    { key: 'time_share_pct', label: 'Share of Total Time (%)' },
    { key: 'load', label: 'Load Level' },
  ], [isMariaDB]);

  const exportExtraParams = useMemo(() => {
    const params: Record<string, any> = {};
    if (userSearchQuery.trim()) {
      params.search = userSearchQuery.trim();
    }
    return params;
  }, [userSearchQuery]);

  // KPI calculations
  const totalCalls = useMemo(() => {
    return userQueryStats.reduce((sum, u) => sum + (Number(u.calls) || 0), 0);
  }, [userQueryStats]);

  const totalTimeMs = useMemo(() => {
    return userQueryStats.reduce((sum, u) => sum + (Number(u.total_time_ms) || 0), 0);
  }, [userQueryStats]);

  const totalCpuMs = useMemo(() => {
    if (summary?.total_cpu_ms !== undefined && summary?.total_cpu_ms !== null) {
      return Number(summary.total_cpu_ms) || 0;
    }
    return userQueryStats.reduce((sum, u) => sum + (Number(u.total_cpu_ms || u.cpu_time_ms) || 0), 0);
  }, [summary, userQueryStats]);

  const PALETTE = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#64748b'];

  const formatTotalTime = (ms?: number | null) => {
    if (ms == null || isNaN(ms)) return '0 ms';
    if (ms >= 3600000) return `${(ms / 3600000).toFixed(2)}h`;
    if (ms >= 60000) return `${(ms / 60000).toFixed(2)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms.toFixed(0)}ms`;
  };

  const formatLatency = (ms?: number | null) => {
    if (ms == null || isNaN(ms)) return '0 ms';
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    if (ms >= 1) return `${ms.toFixed(2)}ms`;
    return `${(ms * 1000).toFixed(1)}µs`;
  };

  const heaviestUser = useMemo(() => {
    if (summary?.heaviest_user) return summary.heaviest_user;
    if (!userQueryStats || userQueryStats.length === 0) return null;
    const sorted = [...userQueryStats].sort((a, b) => (Number(b.total_time_ms) || 0) - (Number(a.total_time_ms) || 0));
    return sorted[0];
  }, [summary, userQueryStats]);

  const avgLatencyMs = useMemo(() => {
    if (summary?.avg_latency_ms != null) return Number(summary.avg_latency_ms);
    return totalCalls > 0 ? totalTimeMs / totalCalls : 0;
  }, [summary, totalCalls, totalTimeMs]);

  const chartData = useMemo(() => {
    if (!userQueryStats || userQueryStats.length === 0) return [];
    return [...userQueryStats]
      .map((u) => {
        const calls = Number(u.calls || 0);
        const totalTime = Number(u.total_time_ms || 0);
        const avgTime = u.avg_time_ms != null ? Number(u.avg_time_ms) : (calls > 0 ? totalTime / calls : 0);

        let metricVal = totalTime;
        if (chartMetric === 'calls') metricVal = calls;
        else if (chartMetric === 'latency') metricVal = avgTime;

        return {
          username: u.username || 'unknown',
          value: Number(metricVal.toFixed(2)),
          rawTotalTime: totalTime,
          rawCalls: calls,
          rawAvgTime: avgTime,
          timeSharePct: u.time_share_pct,
          callsSharePct: u.calls_share_pct,
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [userQueryStats, chartMetric]);

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
            <Users className="w-4 h-4" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">User Query Statistics</h2>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-1">
        {/* Total Database Users */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-md flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 font-semibold uppercase tracking-wider mb-1">Users with activity</div>
            <div className="text-xl font-bold font-mono text-gray-900 dark:text-white">
              {summary?.active_users ?? summary?.total_users ?? userQueryStats.length}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-200 truncate">
              active / total
            </div>
          </div>
          <div className="p-1 rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 text-orange-600 dark:text-orange-400 shrink-0">
            <Users className="w-4 h-4" />
          </div>
        </div>

        {/* Total Execution Calls */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-md flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 font-semibold uppercase tracking-wider mb-1">{isMariaDB ? 'Statements' : 'Queries'}</div>
            <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
              {(summary?.total_calls ?? summary?.total_queries ?? totalCalls).toLocaleString()}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-200 truncate">
              total execution calls
            </div>
          </div>
          <div className="p-1 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 shrink-0">
            <Activity className="w-4 h-4" />
          </div>
        </div>

        {/* Total Execution Time */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-md flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 font-semibold uppercase tracking-wider mb-1">Total execution time</div>
            <div className="text-xl font-bold font-mono text-blue-600 dark:text-blue-400">
              {formatTotalTime(Number(summary?.total_time_ms) || totalTimeMs)}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-200 truncate">
              across all users
            </div>
          </div>
          <div className="p-1 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-blue-600 dark:text-blue-400 shrink-0">
            <Clock className="w-4 h-4" />
          </div>
        </div>

        {/* Total CPU Time */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-md flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 font-semibold uppercase tracking-wider mb-1">Total CPU time</div>
            {summary?.total_cpu_ms === null ? (
              <div className="text-xl font-bold font-mono text-gray-200 dark:text-gray-500" title={summary?.total_cpu_ms_reason || 'Not supported by this database engine'}>
                N/A
              </div>
            ) : (
              <div className="text-xl font-bold font-mono text-purple-600 dark:text-purple-400">
                {formatTotalTime(totalCpuMs)}
              </div>
            )}
            <div className="text-[10px] text-gray-500 dark:text-gray-200 truncate" title={summary?.total_cpu_ms_reason || ''}>
              {summary?.total_cpu_ms === null ? 'engine counter N/A' : 'cumulative CPU'}
            </div>
          </div>
          <div className="p-1 rounded-lg bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 shrink-0">
            <Cpu className="w-4 h-4" />
          </div>
        </div>

        {/* Heaviest User */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-md flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] text-gray-500 dark:text-gray-200 font-semibold uppercase tracking-wider mb-1">Heaviest user</div>
            <div className="text-lg font-bold font-mono text-amber-600 dark:text-amber-400 truncate" title={heaviestUser?.username}>
              {heaviestUser?.username || '—'}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-200 truncate">
              {heaviestUser?.time_share_pct != null
                ? `${heaviestUser.time_share_pct}% time · ${heaviestUser.calls_share_pct ?? 0}% calls`
                : 'Top query consumer'}
            </div>
          </div>
          <div className="p-1 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-600 dark:text-amber-400 shrink-0">
            <Flame className="w-4 h-4" />
          </div>
        </div>

        {/* Average Latency */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-md flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 font-semibold uppercase tracking-wider mb-1">Avg latency</div>
            <div className="text-xl font-bold font-mono text-cyan-600 dark:text-cyan-400">
              {formatLatency(avgLatencyMs)}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-200 truncate">
              per statement / query
            </div>
          </div>
          <div className="p-1 rounded-lg bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 text-cyan-600 dark:text-cyan-400 shrink-0">
            <Timer className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Graph & Workload Insights Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
        {/* Left: Bar Graph */}
        <div className="lg:col-span-7 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 border-b border-gray-100 dark:border-gray-700/50 pb-2.5">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
              <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                User Workload Distribution
              </h3>
            </div>

            {/* Metric Filter Tabs */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-900/90 p-0.5 rounded-lg border border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setChartMetric('time')}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                  chartMetric === 'time'
                    ? 'bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Execution Time
              </button>
              <button
                type="button"
                onClick={() => setChartMetric('calls')}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                  chartMetric === 'calls'
                    ? 'bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Query Calls
              </button>
              <button
                type="button"
                onClick={() => setChartMetric('latency')}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                  chartMetric === 'latency'
                    ? 'bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Avg Latency
              </button>
            </div>
          </div>

          <div className="h-64 w-full flex items-center justify-center">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={chartData}
                  margin={{ top: 5, right: 25, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="currentColor" className="text-gray-100 dark:text-gray-700/50" />
                  <XAxis
                    type="number"
                    tick={{ fill: 'currentColor', fontSize: 10 }}
                    className="text-gray-500 dark:text-gray-200 font-mono"
                    tickFormatter={(val) => {
                      if (chartMetric === 'time') return formatTotalTime(val);
                      if (chartMetric === 'calls') return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(val);
                      return formatLatency(val);
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="username"
                    tick={{ fill: 'currentColor', fontSize: 11 }}
                    className="text-gray-700 dark:text-gray-300 font-mono font-medium"
                    width={90}
                    axisLine={false}
                    tickLine={false}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const item = payload[0].payload;
                      return (
                        <div className="bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur border border-gray-700 rounded-lg p-2.5 shadow-xl text-xs space-y-1 font-mono text-white z-50">
                          <div className="font-bold text-orange-400 border-b border-gray-700 pb-1">{item.username}</div>
                          {chartMetric === 'time' && (
                            <div className="text-gray-300 flex justify-between gap-4">
                              <span>Execution Time:</span>
                              <span className="font-semibold text-blue-400">
                                {formatTotalTime(item.rawTotalTime)} {item.timeSharePct != null ? `(${item.timeSharePct}%)` : ''}
                              </span>
                            </div>
                          )}
                          {chartMetric === 'calls' && (
                            <div className="text-gray-300 flex justify-between gap-4">
                              <span>Calls:</span>
                              <span className="font-semibold text-emerald-400">
                                {item.rawCalls.toLocaleString()} {item.callsSharePct != null ? `(${item.callsSharePct}%)` : ''}
                              </span>
                            </div>
                          )}
                          {chartMetric === 'latency' && (
                            <div className="text-gray-300 flex justify-between gap-4">
                              <span>Avg Latency:</span>
                              <span className="font-semibold text-amber-400">{formatLatency(item.rawAvgTime)}</span>
                            </div>
                          )}
                        </div>
                      );
                    }}

                    cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {chartData.map((_, idx) => (
                      <Cell key={`cell-${idx}`} fill={PALETTE[idx % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No user workload data available</span>
            )}
          </div>
        </div>

        {/* Right: Insights Card */}
        <div className="lg:col-span-5 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-100 dark:border-gray-700/50 pb-2.5">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-amber-500" />
                <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                  Workload Insights
                </h3>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
                {insights.length} insight{insights.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="space-y-2.5 overflow-y-auto max-h-[250px] pr-1 custom-scroll">
              {insights.length > 0 ? (
                insights.map((item, idx) => {
                  const isWarning = item.severity === 'warning';
                  const isCritical = item.severity === 'critical';

                  const icon = item.type === 'HOTSPOT' ? (
                    <Flame className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  ) : item.type === 'SLOWEST' ? (
                    <Clock className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                  ) : item.type === 'EFFICIENT' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  ) : item.type === 'CPU' ? (
                    <Cpu className="w-4 h-4 text-purple-500 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                  );

                  return (
                    <div
                      key={idx}
                      className={`p-2.5 rounded-lg border text-xs flex items-start gap-2.5 transition-all ${
                        isCritical
                          ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20 text-rose-900 dark:text-rose-200'
                          : isWarning
                          ? 'bg-amber-50/70 dark:bg-amber-500/10 border-amber-200/80 dark:border-amber-500/20 text-amber-900 dark:text-amber-200'
                          : 'bg-blue-50/70 dark:bg-blue-500/10 border-blue-200/80 dark:border-blue-500/20 text-blue-900 dark:text-blue-200'
                      }`}
                    >
                      {icon}
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold font-mono uppercase text-[9px] px-1.5 py-0.2 rounded bg-white/70 dark:bg-gray-900/60 border border-black/5 dark:border-white/10">
                            {item.type}
                          </span>
                          {item.username && (
                            <span className="font-mono text-[11px] font-semibold text-gray-800 dark:text-white">
                              @{item.username}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-relaxed">
                          {item.message}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-12 text-center text-xs text-gray-500 dark:text-gray-200 italic">
                  No workload insights or anomalies reported.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 text-orange-500 dark:text-orange-400" />
            <h3 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Query activity by user ({filteredAndSortedStats.length})
            </h3>
          </div>

          <div className="flex items-center gap-2.5">
            {/* Search by User */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search user..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500/50 w-56 h-8"
              />
              {userSearchQuery && (
                <button
                  onClick={() => setUserSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <DBExportModal
              endpoint="/db_monitor_dashboard/user_query_stats"
              columns={exportColumns}
              cluster_id={activeCluster}
              client_id={clientId}
              extraParams={exportExtraParams}
              defaultSortBy="total_time_ms"
              defaultSortOrder="desc"
              buttonText="Export"
              title="Export Query Activity by User"
              subtitle="Export user query execution statistics and workload metrics"
            />
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-[480px] border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-500 dark:text-gray-200 uppercase tracking-wider select-none">
                <th
                  onClick={() => handleSort('username')}
                  className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900 cursor-pointer text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>User</span>
                    <SortIcon field="username" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('calls')}
                  className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>{isMariaDB ? 'Statements' : 'Queries'}</span>
                    <SortIcon field="calls" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('avg_time')}
                  className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Avg Time</span>
                    <SortIcon field="avg_time" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('total_time')}
                  className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Total Time</span>
                    <SortIcon field="total_time" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('cpu_time')}
                  className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>CPU Time</span>
                    <SortIcon field="cpu_time" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('share')}
                  className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Share of total time</span>
                    <SortIcon field="share" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {filteredAndSortedStats && filteredAndSortedStats.length > 0 ? (
                filteredAndSortedStats.map((u, idx) => {
                  const calls = Number(u.calls || 0);
                  const totalMs = Number(u.total_time_ms || 0);
                  const cpuMs = Number(u.total_cpu_ms ?? u.cpu_time_ms ?? 0);
                  const avgMs = u.avg_time_ms != null ? Number(u.avg_time_ms) : (calls > 0 ? totalMs / calls : 0);
                  const avgStr = formatLatency(avgMs);
                  const totalStr = formatTotalTime(totalMs);
                  const cpuStr = (u.total_cpu_ms !== null && u.total_cpu_ms !== undefined) || (u.cpu_time_ms !== null && u.cpu_time_ms !== undefined)
                    ? formatTotalTime(cpuMs)
                    : '—';
                  const baseTotalTime = Number(summary?.total_time_ms) || totalTimeMs || 0;
                  const sharePct = u.time_share_pct != null ? Number(u.time_share_pct) : (baseTotalTime > 0 ? (totalMs / baseTotalTime) * 100 : 0);
                  const shareStr = sharePct === 0 ? '0%' : sharePct < 0.1 ? '<0.1%' : `${sharePct.toFixed(1)}%`;

                  return (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                      <td className="py-2.5 px-3.5 font-mono text-gray-900 dark:text-gray-200 font-medium">
                        <div className="flex items-center gap-2">
                          <span>{u.username || '-'}</span>
                          {u.load && (
                            <span
                              className={`px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider ${
                                u.load === 'HEAVY'
                                  ? 'bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30'
                                  : u.load === 'MODERATE'
                                  ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30'
                                  : 'bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-200 border border-slate-200 dark:border-gray-700'
                              }`}
                            >
                              {u.load}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                        {calls.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-mono text-emerald-600 dark:text-emerald-400 font-semibold">
                        {avgStr}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-mono text-blue-600 dark:text-blue-400 font-bold">
                        {totalStr}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-mono text-purple-600 dark:text-purple-400 font-semibold">
                        {cpuStr}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{shareStr}</span>
                          <div className="w-12 bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden shrink-0">
                            <div
                              className="bg-blue-500 h-full rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(sharePct, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-200 dark:text-gray-500 italic">
                    {userSearchQuery
                      ? `No user query statistics matching "${userSearchQuery}".`
                      : 'No user query statistics available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserQueryStatisticsView;

